import axios, {
  AxiosError,
  AxiosHeaders,
  InternalAxiosRequestConfig,
} from "axios";
import * as Sentry from "@sentry/browser";

import { getEnvConfig } from "@/env";
import { staticAntd } from "@/utils/antdStatic";

declare module "axios" {
  // Per-request opt-out for the 401 reauth reload below. Set on
  // fire-and-forget polling endpoints whose failures shouldn't drive auth,
  // otherwise their cadence turns a single auth failure into a reload loop.
  interface AxiosRequestConfig {
    silenceReauth?: boolean;
    // Internal to the interceptors below: who the request was sent as, and
    // whether it has already been retried after a cross-tab token change.
    _authUsername?: string;
    _authRetried?: boolean;
  }
}

const config = getEnvConfig();
const isBrowserTest = localStorage.getItem("is_browser_test") === "true";
const testServerUrl = localStorage.getItem("live_server");

export const apiUrl =
  config.environment === "local" && isBrowserTest
    ? testServerUrl
    : config.apiUrl;

export const internalAPI = axios.create({
  baseURL: `${apiUrl}api/internal/v1/`,
  timeout: 600000,
  withCredentials: true,
});

export const externalAPI = axios.create({
  baseURL: `${apiUrl}api/external/v1/`,
  timeout: 600000,
  withCredentials: true,
});

const REAUTH_RELOAD_GUARD_KEY = "auth_reload_loop_guard";
const REAUTH_RELOAD_WINDOW_MS = 30_000;
const REAUTH_RELOAD_MAX = 3;

// Stops the reauth reload from looping forever when silent reauth on the
// reloaded page isn't producing a fresh token. After REAUTH_RELOAD_MAX
// reloads inside REAUTH_RELOAD_WINDOW_MS we stop reloading, so we don't
// hammer the auth rate-limiter or trap the user in a refresh storm.
// A successful response clears the guard.
function shouldReloadForReauth(): boolean {
  try {
    const raw = sessionStorage.getItem(REAUTH_RELOAD_GUARD_KEY);
    const now = Date.now();
    let firstAt = now;
    let count = 0;
    if (raw) {
      const parsed = JSON.parse(raw) as { firstAt?: number; count?: number };
      if (
        typeof parsed.firstAt === "number" &&
        typeof parsed.count === "number" &&
        now - parsed.firstAt < REAUTH_RELOAD_WINDOW_MS
      ) {
        firstAt = parsed.firstAt;
        count = parsed.count;
      }
    }
    if (count >= REAUTH_RELOAD_MAX) {
      return false;
    }
    sessionStorage.setItem(
      REAUTH_RELOAD_GUARD_KEY,
      JSON.stringify({ firstAt, count: count + 1 }),
    );
    return true;
  } catch {
    return true;
  }
}

function getStoredUser(): { token?: string; username?: string } {
  return JSON.parse(localStorage.getItem("user") || "{}");
}

export function getStoredToken(): string | undefined {
  return getStoredUser().token;
}

const STORED_TOKEN_POLL_MS = 5_000;

// Resolves with the stored token once there is one. Another tab storing its
// fresh token raises a `storage` event here; the poll covers a token stored by
// this tab, which raises none.
export function waitForStoredToken(): Promise<string> {
  const token = getStoredToken();
  if (token) {
    return Promise.resolve(token);
  }
  return new Promise((resolve) => {
    const check = () => {
      const storedToken = getStoredToken();
      if (storedToken) {
        window.clearInterval(poll);
        window.removeEventListener("storage", check);
        resolve(storedToken);
      }
    };
    const poll = window.setInterval(check, STORED_TOKEN_POLL_MS);
    window.addEventListener("storage", check);
  });
}

// Set once an auth rejection has triggered the reauth reload: the page is
// going away, so later rejections from the same burst neither count towards
// the loop guard nor reach their callers as errors.
let reauthReloadPending = false;

export function isReauthReloadPending(): boolean {
  return reauthReloadPending;
}

export type AuthRejectionOutcome =
  // A reauth reload is already under way; nothing more to do.
  | "reload-pending"
  // Nothing is stored: an anonymous request, or another tab already dropped
  // the session.
  | "no-session"
  // The rejected credential is not the stored one; retry with the stored one.
  | "stale-token"
  // The stored token was dropped and the page is reloading.
  | "reloading"
  // The stored token was dropped, but the loop guard stopped the reload.
  | "loop-detected";

// Shared by the HTTP 401 interceptor below and the WebSocket contexts (close
// code 4001). `sentToken` is the token the rejected request or socket carried.
export function handleAuthRejection(
  sentToken: string | undefined,
): AuthRejectionOutcome {
  if (reauthReloadPending) {
    return "reload-pending";
  }
  const storedToken = getStoredToken();
  if (!storedToken) {
    return "no-session";
  }
  // localStorage is shared by every tab. When another tab re-authenticated
  // while this request was in flight, the request carried an older token
  // (or none) and the stored one is fresh. Dropping it here logged the
  // other tab out, which then did the same to this one: a reload loop.
  if (sentToken !== storedToken) {
    return "stale-token";
  }
  // The backend issues DRF tokens with a 24h TTL. When a token expires
  // mid-session the backend rejects it — drop the stale token and
  // reload so the boot flow (MSAL silent → validate_ad_token) can
  // mint a fresh one or fall back to interactive Microsoft login.
  localStorage.removeItem("user");
  if (shouldReloadForReauth()) {
    reauthReloadPending = true;
    window.location.reload();
    return "reloading";
  }
  const loopMessage =
    "Auth reload loop detected: silent reauth is not refreshing the " +
    "token. Stopping reloads — clear localStorage and reload manually.";
  console.error(loopMessage);
  // 401s themselves are filtered out of Sentry (see index.tsx), so report
  // the one case where reauth is genuinely broken.
  Sentry.captureMessage(loopMessage, "error");
  return "loop-detected";
}

for (const api of [internalAPI, externalAPI]) {
  api.interceptors.request.use((reqConfig: InternalAxiosRequestConfig) => {
    const { token, username } = getStoredUser();

    if (token) {
      const headers = {
        ...reqConfig.headers,
        Authorization: `Token ${token}`,
      };
      return {
        ...reqConfig,
        headers,
        _authUsername: username,
      } as InternalAxiosRequestConfig;
    }

    return reqConfig;
  });

  api.interceptors.response.use(
    (response) => {
      // A request that ended without any HTTP response has status 0, and
      // axios resolves it as a success with `data: ""` (its `settle` only
      // rejects a non-zero status, for file:// URLs). Firefox ends in-flight
      // XHRs that way while the page unloads — e.g. during the reauth reload
      // below — and callers expecting a list crashed on `"".map`. Reject it
      // like the network failure it is.
      if (response.status === 0) {
        return Promise.reject(
          new AxiosError(
            "Request ended without a response",
            AxiosError.ERR_NETWORK,
            response.config,
            response.request,
          ),
        );
      }
      sessionStorage.removeItem(REAUTH_RELOAD_GUARD_KEY);
      return response;
    },
    (error: AxiosError) => {
      if (
        error.response?.status !== 401 ||
        !error.config ||
        error.config.silenceReauth
      ) {
        return Promise.reject(error);
      }
      const sentAuthorization = AxiosHeaders.from(error.config.headers).get(
        "Authorization",
      );
      const sentToken =
        typeof sentAuthorization === "string" &&
        sentAuthorization.startsWith("Token ")
          ? sentAuthorization.slice("Token ".length)
          : undefined;
      switch (handleAuthRejection(sentToken)) {
        case "reload-pending":
        case "reloading":
          // An expired session isn't an error for the caller to report.
          return new Promise(() => {});
        case "stale-token":
          // Retry once, and never as someone else: after a sign-out and a
          // sign-in as another user in another tab, replaying the request
          // (possibly a POST) would perform it as that other user.
          if (
            !error.config._authRetried &&
            (!sentToken ||
              error.config._authUsername === getStoredUser().username)
          ) {
            error.config._authRetried = true;
            return api.request(error.config);
          }
      }
      return Promise.reject(error);
    },
  );
}

export interface ApiErrors {
  non_field_errors?: string[];
  [key: string]: any;
}

export function getErrorsFromResponse(
  error: AxiosError<any>,
): ApiErrors | undefined {
  if (error.message === "Canceled") {
    return;
  }

  const { response } = error;

  if (!response) {
    console.error(error);
    return;
  }

  let errors: ApiErrors = {};

  if (response.data.detail) {
    errors.non_field_errors = [response.data.detail];
  } else {
    errors = response ? response.data.errors || response.data : error;
  }

  return errors;
}

export function getErrorMessage(error: AxiosError<any>): string {
  // Always surface something. Earlier this helper skipped the notification
  // when the response body was empty (e.g. a proxy-level 502 with no body),
  // which made gateway errors look like a silent success on screen.
  let errorMessage = "Unexpected error";

  if (error.response) {
    const { status, data } = error.response;
    // Used only when the body carries nothing worth showing. A 404 with a body
    // describes *what* was missing (e.g. "The requested Octane customer was not
    // found."); treating every 404 as a routing problem threw that away and sent
    // people looking for a broken route instead of missing data.
    const fallback =
      status === 404
        ? "404 - Route not found"
        : `Request failed with status ${status}`;
    if (data && typeof data === "object") {
      if (data.non_field_errors) {
        errorMessage = data.non_field_errors.join(", ");
      } else if (data.detail) {
        errorMessage = data.detail;
      } else if (Object.keys(data).length > 0) {
        errorMessage = JSON.stringify(data);
      } else {
        errorMessage = fallback;
      }
    } else if (typeof data === "string" && data.trim()) {
      // Plain-text body (e.g. nginx/Cloudflare 5xx HTML, or Django's 404 page).
      // Keep it short so we don't dump a whole error page into a toast.
      errorMessage = data.length > 300 ? fallback : data;
    } else {
      errorMessage = fallback;
    }
  } else if (error.request) {
    errorMessage = "Network error — no response from server";
  } else if (error.message) {
    errorMessage = error.message;
  }

  return errorMessage;
}

/**
 * True when a rejected request carries per-field errors a form can render.
 *
 * `getErrorsFromResponse` maps a DRF `{"detail": ...}` body — 403s, throttling,
 * most non-field 400s — onto `non_field_errors`, which no form in the app
 * renders, and returns `undefined` for a network failure or a non-JSON body
 * (an HTML gateway page). In all of those `setFormErrors` puts nothing on
 * screen, so callers need to know the difference between "the form is showing
 * the problem" and "nobody is".
 */
export function hasRenderableFieldErrors(error: AxiosError<any>): boolean {
  const errors = getErrorsFromResponse(error);
  if (errors == null || Array.isArray(errors) || typeof errors !== "object") {
    return false;
  }
  return Object.keys(errors).some((field) => field !== "non_field_errors");
}

export function onLoadingError(error: AxiosError<any>, msg?: string): void {
  staticAntd.notification?.error({
    message: msg || "Error",
    description: getErrorMessage(error),
    duration: null,
  });

  console.error(error);
  console.trace();
}
